'use client';

import { useRouter } from 'next/navigation';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { StaticImageData } from 'next/image';

import { CategoryDetail, CreateDonationRequestModeEnum, Donation, Project, User, Reward, Subproject } from '@/app/api';
import { SelectOption } from '@/app/components/Select/Select';
import { Country, DonationFormType, Language, PaymentMethod } from '@/app/enums';
import { getCountryConfig } from '@/app/utils/countryConfig';

import animalsCategoryIcon from '@/public/public/images/animals.png';
import educationCategoryIcon from '@/public/public/images/book.png';
import kidsAndFamilyCategoryIcon from '@/public/public/images/children.png';
import communityCategoryIcon from '@/public/public/images/community.png';
import environmentCategoryIcon from '@/public/public/images/eco.png';
import elderlyCategoryIcon from '@/public/public/images/elderly.png';
import healthCategoryIcon from '@/public/public/images/health.png';
import humanitarianHelpCategoryIcon from '@/public/public/images/help.png';
import damageCategoryIcon from '@/public/public/images/mountains.png';
import cultureCategoryIcon from '@/public/public/images/music.png';
import myChoiceCategoryIcon from '@/public/public/images/story.png';

import QRCodeIcon from '@/app/components/QRCodeIcon/QRCodeIcon';
import { CardLogos } from '@/app/components/DonationForm/CardLogos/CardLogos';
import { RadioGroupOption } from '@/app/components/FormRadioGroup/FormRadioGroup';

import GoodHabitDonationFormProps, {
  GoodHabitDonationFormValuesAuthenticatedUser,
  GoodHabitDonationFormValuesUnauthenticatedUser,
} from '@/app/components/DonationForm/GoodHabitDonationForm/GoodHabitDonationForm';

import LongTermDonationFormProps, {
  LongTermDonationFormValuesAuthenticatedUser,
  LongTermDonationFormValuesUnauthenticatedUser,
} from '@/app/components/DonationForm/LongTermDonationForm/LongTermDonationForm';

import SingleDonationFormProps, {
  SingleDonationFormValuesAuthenticatedUser,
  SingleDonationFormValuesUnauthenticatedUser,
} from '@/app/components/DonationForm/SingleDonationForm/SingleDonationForm';

import CommercialProjectDonationForm, {
  CommercialProjectDonationFormValues,
} from '@/app/components/DonationForm/CommercialProjectDonationForm/CommercialProjectDonationForm';

interface SingleDonationFormProps {
  currentUser?: User;
  formType: DonationFormType.SINGLE_AUTHENTICATED_USER | DonationFormType.SINGLE_UNAUTHENTICATED_USER;
  project: Project;
  subproject?: Subproject;
  unassignedGoodhabits?: Donation[] | null;
  initialValues?: Partial<SingleDonationFormValuesAuthenticatedUser | SingleDonationFormValuesUnauthenticatedUser>;
  shouldDisplayPrivacyConsentCheckbox?: boolean;
  amountOptions: RadioGroupOption<number>[];
}

interface LongTermDonationFormProps {
  currentUser?: User;
  formType: DonationFormType.LONG_TERM_AUTHENTICATED_USER | DonationFormType.LONG_TERM_UNAUTHENTICATED_USER;
  project: Project;
  subproject?: Subproject;
  unassignedGoodhabits?: Donation[] | null;
  initialValues?: Partial<LongTermDonationFormValuesAuthenticatedUser | LongTermDonationFormValuesUnauthenticatedUser>;
  shouldDisplayPrivacyConsentCheckbox?: boolean;
  amountOptions: RadioGroupOption<number>[];
}

interface GoodHabitDonationFormProps {
  currentUser?: User;
  formType: DonationFormType.GOOD_HABIT_AUTHENTICATED_USER | DonationFormType.GOOD_HABIT_UNAUTHENTICATED_USER;
  project: Project;
  subproject?: Subproject;
  categories: CategoryDetail[];
  regularDonationFundsAvailable?: Donation[];
  initialValues?: Partial<
    GoodHabitDonationFormValuesAuthenticatedUser | GoodHabitDonationFormValuesUnauthenticatedUser
  >;
  isCategoriesSelectVisible?: boolean;
  shouldDisplayPrivacyConsentCheckbox?: boolean;
  unassignedGoodhabits?: Donation[] | null;
  amountOptions: RadioGroupOption<number>[];
}

interface CommercialProjectFormProps {
  currentUser?: User;
  formType: DonationFormType.COMMERCIAL_PROJECT;
  project: Project;
  subproject?: Subproject;
  initialValues?: Partial<CommercialProjectDonationFormValues>;
  shouldDisplayPrivacyConsentCheckbox?: boolean;
  rewards?: Reward[];
}

export const categorySlugToIconMap: Record<string, StaticImageData> = {
  'deti-rodina': kidsAndFamilyCategoryIcon,
  'humanitarni-pomoc': humanitarianHelpCategoryIcon,
  komunita: communityCategoryIcon,
  kultura: cultureCategoryIcon,
  priroda: environmentCategoryIcon,
  seniori: elderlyCategoryIcon,
  'skody-na-majetku': damageCategoryIcon,
  vzdelavani: educationCategoryIcon,
  zdravi: healthCategoryIcon,
  zvirata: animalsCategoryIcon,
};

const countryConfig = getCountryConfig();

const DonationForm = (
  props: SingleDonationFormProps | LongTermDonationFormProps | GoodHabitDonationFormProps | CommercialProjectFormProps,
) => {
  const router = useRouter();

  const {
    t,
    i18n: { language },
  } = useTranslation();

  const countrySelectOptions = useMemo(() => {
    // TODO add to env variables?
    const highlightedCountries = [Country.CZECH_REPUBLIC, Country.SLOVAKIA];

    const allCountryOptions = Object.values(Country).map((countryCode) => ({
      value: countryCode,
      label: t(`enums.countries.${countryCode}`),
    }));

    const highlightedOptions = allCountryOptions
      .filter((option) => highlightedCountries.includes(option.value))
      .map((option) => ({ ...option, highlighted: true }));

    const remainingOptions = allCountryOptions.filter((option) => !highlightedCountries.includes(option.value));

    return [{ value: undefined, label: '', highlighted: true }, ...highlightedOptions, ...remainingOptions];
  }, [t]);

  const donationModeOptions = useMemo(() => {
    return [
      { value: CreateDonationRequestModeEnum.Single, label: t('components.DonationForm.oneTimeDonation') },
      { value: CreateDonationRequestModeEnum.LongTerm, label: t('components.DonationForm.regularDonation') },
    ];
  }, [t]);

  const categoryOptions = useMemo(() => {
    if (
      props.formType !== DonationFormType.GOOD_HABIT_AUTHENTICATED_USER &&
      props.formType !== DonationFormType.GOOD_HABIT_UNAUTHENTICATED_USER
    ) {
      return [];
    }

    const categoriesFromApi = props.categories.reduce(
      (acc, category) => {
        if (!category.id || !category.slug || !category.name) {
          return acc;
        }

        return [
          ...acc,
          {
            value: category.id,
            label: category.name,
            image: categorySlugToIconMap[category.slug],
          },
        ];
      },
      [] as (SelectOption<string> & { image: StaticImageData })[],
    );

    return [
      { value: undefined, label: t('enums.donationFields.myChoice'), image: myChoiceCategoryIcon },
      ...categoriesFromApi,
    ];
  }, [props, t]);

  const getPaymentMethodOptions = useCallback(
    (donationMode: CreateDonationRequestModeEnum | null) => {
      if (donationMode === CreateDonationRequestModeEnum.LongTerm) {
        return [
          {
            value: PaymentMethod.WireTransfer,
            label: t('components.DonationForm.standingOrder'),
          },
          {
            value: PaymentMethod.Card,
            label: t('components.DonationForm.creditOrDebitCard'),
            noteWhenSelected: t('components.DonationForm.regularCardPaymentNote'),
            // Long term project does not support Apple Pay or Google Pay
            children: (
              <CardLogos isApplePaySupportedByPaymentGateway={false} isGooglePaySupportedByPaymentGateway={false} />
            ),
          },
        ].filter((option) => countryConfig.charityCollections.availablePaymentMethods.includes(option.value));
      }

      const creditCardOption = {
        value: PaymentMethod.Card,
        label: t('components.DonationForm.creditOrDebitCard'),
        children: <CardLogos isApplePaySupportedByPaymentGateway isGooglePaySupportedByPaymentGateway />,
      };

      const wireTransferOption = {
        value: PaymentMethod.WireTransfer,
        label: t('components.DonationForm.bankTransfer'),
        children: (
          <div className="mt-[12px] ml-[6px] text-[16px] text-neutral-semiTransparent leading-[20px]">
            {t('components.DonationForm.wireTransferNote')}
          </div>
        ),
      };

      const qrCodeOption = {
        value: PaymentMethod.QrCode,
        label: t('components.DonationForm.qrCodePayment'),
        children: (
          <div className="flex items-center mt-[8px] ml-[6px] text-[16px] text-neutral-semiTransparent leading-[20px]">
            <QRCodeIcon className="mr-[4px] text-[#d9d9d9]" />
            {t('components.DonationForm.qrCodePaymentNote')}
          </div>
        ),
      };

      switch (props.formType) {
        case DonationFormType.LONG_TERM_UNAUTHENTICATED_USER:
        case DonationFormType.LONG_TERM_AUTHENTICATED_USER:
          return [wireTransferOption, creditCardOption, qrCodeOption].filter((option) =>
            countryConfig.charityCollections.availablePaymentMethods.includes(option.value),
          );

        case DonationFormType.COMMERCIAL_PROJECT:
          return [creditCardOption, wireTransferOption, qrCodeOption].filter((option) =>
            countryConfig.commercialProjects.availablePaymentMethods.includes(option.value),
          );

        case DonationFormType.GOOD_HABIT_AUTHENTICATED_USER:
        case DonationFormType.GOOD_HABIT_UNAUTHENTICATED_USER:
          return [creditCardOption, wireTransferOption, qrCodeOption].filter((option) => {
            if (!countryConfig.goodHabit.enabled) {
              return option;
            }

            return countryConfig.goodHabit.availablePaymentMethods.includes(option.value);
          });

        case DonationFormType.SINGLE_AUTHENTICATED_USER:
        case DonationFormType.SINGLE_UNAUTHENTICATED_USER:
        default:
          return [creditCardOption, wireTransferOption, qrCodeOption].filter((option) =>
            countryConfig.charityCollections.availablePaymentMethods.includes(option.value),
          );
      }
    },
    [props.formType, t],
  );

  const handleCloseButtonClick = useCallback(() => {
    const languageQuery = language === Language.EN ? `?lang=${language}` : '';

    window.location.href = `${process.env.NEXT_PUBLIC_REDIRECT_URL}/${props.project.slug}${languageQuery}`;
  }, [props.project, router, language]);

  switch (props.formType) {
    case DonationFormType.GOOD_HABIT_UNAUTHENTICATED_USER:
      return (
        <GoodHabitDonationFormProps<GoodHabitDonationFormValuesUnauthenticatedUser>
          project={props.project}
          subproject={props.subproject}
          countrySelectOptions={countrySelectOptions}
          categoryOptions={categoryOptions}
          currentUser={props.currentUser}
          isCategoriesSelectVisible={props.isCategoriesSelectVisible}
          getPaymentMethodOptions={getPaymentMethodOptions}
          onCloseButtonClick={handleCloseButtonClick}
          initialValues={props.initialValues as GoodHabitDonationFormValuesUnauthenticatedUser}
        />
      );

    case DonationFormType.GOOD_HABIT_AUTHENTICATED_USER:
      return (
        <GoodHabitDonationFormProps<GoodHabitDonationFormValuesAuthenticatedUser>
          project={props.project}
          subproject={props.subproject}
          countrySelectOptions={countrySelectOptions}
          categoryOptions={categoryOptions}
          currentUser={props.currentUser}
          isCategoriesSelectVisible={props.isCategoriesSelectVisible}
          getPaymentMethodOptions={getPaymentMethodOptions}
          onCloseButtonClick={handleCloseButtonClick}
          initialValues={props.initialValues as GoodHabitDonationFormValuesAuthenticatedUser}
        />
      );

    case DonationFormType.LONG_TERM_UNAUTHENTICATED_USER:
      return (
        <LongTermDonationFormProps<LongTermDonationFormValuesUnauthenticatedUser>
          shouldDisplayPrivacyConsentCheckbox={props.shouldDisplayPrivacyConsentCheckbox}
          countrySelectOptions={countrySelectOptions}
          amountOptions={props.amountOptions}
          donationModeOptions={donationModeOptions}
          getPaymentMethodOptions={getPaymentMethodOptions}
          project={props.project}
          subproject={props.subproject}
          currentUser={props.currentUser}
          onCloseButtonClick={handleCloseButtonClick}
          initialValues={props.initialValues as LongTermDonationFormValuesUnauthenticatedUser}
          unassignedGoodhabits={props.unassignedGoodhabits}
        />
      );

    case DonationFormType.LONG_TERM_AUTHENTICATED_USER:
      return (
        <LongTermDonationFormProps<LongTermDonationFormValuesAuthenticatedUser>
          shouldDisplayPrivacyConsentCheckbox={props.shouldDisplayPrivacyConsentCheckbox}
          unassignedGoodhabits={props.unassignedGoodhabits}
          countrySelectOptions={countrySelectOptions}
          amountOptions={props.amountOptions}
          donationModeOptions={donationModeOptions}
          getPaymentMethodOptions={getPaymentMethodOptions}
          project={props.project}
          subproject={props.subproject}
          currentUser={props.currentUser}
          onCloseButtonClick={handleCloseButtonClick}
          initialValues={props.initialValues as LongTermDonationFormValuesAuthenticatedUser}
        />
      );

    case DonationFormType.COMMERCIAL_PROJECT:
      return (
        <CommercialProjectDonationForm
          shouldDisplayPrivacyConsentCheckbox={props.shouldDisplayPrivacyConsentCheckbox}
          countrySelectOptions={countrySelectOptions}
          getPaymentMethodOptions={getPaymentMethodOptions}
          project={props.project}
          subproject={props.subproject}
          currentUser={props.currentUser}
          rewards={props.rewards}
          onCloseButtonClick={handleCloseButtonClick}
          initialValues={props.initialValues as CommercialProjectDonationFormValues}
        />
      );

    case DonationFormType.SINGLE_UNAUTHENTICATED_USER:
      return (
        <SingleDonationFormProps<SingleDonationFormValuesUnauthenticatedUser>
          shouldDisplayPrivacyConsentCheckbox={props.shouldDisplayPrivacyConsentCheckbox}
          countrySelectOptions={countrySelectOptions}
          amountOptions={props.amountOptions}
          getPaymentMethodOptions={getPaymentMethodOptions}
          project={props.project}
          subproject={props.subproject}
          currentUser={props.currentUser}
          unassignedGoodhabits={props.unassignedGoodhabits}
          onCloseButtonClick={handleCloseButtonClick}
          initialValues={props.initialValues as SingleDonationFormValuesUnauthenticatedUser}
        />
      );

    case DonationFormType.SINGLE_AUTHENTICATED_USER:
    default:
      return (
        <SingleDonationFormProps<SingleDonationFormValuesAuthenticatedUser>
          shouldDisplayPrivacyConsentCheckbox={props.shouldDisplayPrivacyConsentCheckbox}
          countrySelectOptions={countrySelectOptions}
          amountOptions={props.amountOptions}
          getPaymentMethodOptions={getPaymentMethodOptions}
          project={props.project}
          subproject={props.subproject}
          currentUser={props.currentUser}
          unassignedGoodhabits={props.unassignedGoodhabits}
          onCloseButtonClick={handleCloseButtonClick}
          initialValues={props.initialValues as SingleDonationFormValuesAuthenticatedUser}
        />
      );
  }
};

export default DonationForm;
